/**
 * Signing in with a mobile number and a code sent to it.
 *
 * Both calls are reachable without a session — that is the whole point of
 * them — so they live here rather than in the portal's data layer, and nothing
 * a customer owns is imported by the login screen.
 *
 * The code is never stored. It is typed, posted, and gone: no `localStorage`,
 * no `sessionStorage`, no cookie, no URL. The challenge id is held in React
 * state for the life of the form and is not a credential on its own — without
 * the code it opens nothing.
 */
import { request } from '../api/client'

export interface OtpChallenge {
  challenge_id: string
  /** Masked by the server. Shown back so somebody can see they typed it right. */
  mobile: string
  expires_in: number
  resend_after?: number
  detail: string
}

export interface DevelopmentCode {
  code: string
  mobile: string
  delivery: string
  notice: string
}

export interface SignedIn {
  id: string
  mobile: string
  email: string | null
  full_name: string
}

export function requestCode(mobile: string, captchaToken: string): Promise<OtpChallenge> {
  return request<OtpChallenge>('/auth/otp/request/', { method: 'POST', body: { mobile, captcha_token: captchaToken } })
}

export function verifyCode(challengeId: string, code: string): Promise<SignedIn> {
  return request<SignedIn>('/auth/otp/verify/', {
    method: 'POST',
    body: { challenge_id: challengeId, code },
  })
}

/**
 * The code for a challenge, when nothing was actually texted.
 *
 * Only exists while the server is in its development delivery mode; anywhere
 * else the path is absent and this resolves to null rather than throwing, so
 * the login screen has nothing to render and says nothing about it.
 */
export async function developmentCode(challengeId: string): Promise<DevelopmentCode | null> {
  try {
    return await request<DevelopmentCode>(`/auth/otp/development/${challengeId}/`)
  } catch {
    return null
  }
}

export function resendCode(challengeId: string, captchaToken: string): Promise<OtpChallenge> {
  return request<OtpChallenge>('/auth/otp/resend/', { method: 'POST', body: { challenge_id: challengeId, captcha_token: captchaToken } })
}
